import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import type { Platform } from "@torre/expenses";
import { useTheme } from "../../../context/ThemeContext";
import { apiErrorMessage, attachmentUrl } from "../services/expensesService";
import {
  confirmPlatformInvoice,
  deletePlatformInvoice,
  fetchPlatformInvoices,
  uploadPlatformInvoices,
  type PlatformInvoiceRow,
} from "../services/platformBillingService";
import { Chip, Dialog, ErrorBox, Field, FileDrop, Notice, Spinner } from "./ui";
import { money } from "../lib/format";
import { KIND_TONES } from "../lib/platformBilling";
import { ui } from "../theme";
import { PLATFORMS_NS } from "../locales/platformsI18n";

// Subir facturas de plataformas y confirmar su liga (#957): la IA (o el XML)
// lee la factura, se propone plataforma y cargo, y la persona confirma.

const NO_CHARGE = "";
const CREATE = "__create__";

type Draft = {
  platformId: string;
  charge: string;
  vendor: string;
  invoiceNumber: string;
  issueDate: string;
  total: string;
  currency: string;
};

const draftOf = (r: PlatformInvoiceRow): Draft => ({
  platformId: r.platformId || r.proposal?.platformId || "",
  charge: r.proposal?.expenseId || NO_CHARGE,
  vendor: r.data?.vendor || "",
  invoiceNumber: r.data?.invoiceNumber || "",
  issueDate: r.data?.issueDate || "",
  total: r.data?.total != null ? String(r.data.total) : "",
  currency: r.data?.currency || "USD",
});

export default function PlatformInvoicesDialog({
  open,
  platforms,
  onClose,
  onChanged,
}: {
  open: boolean;
  platforms: Platform[];
  onClose: () => void;
  onChanged: () => void;
}) {
  const { t: tp, i18n } = useTranslation(PLATFORMS_NS);
  const { t } = useTranslation("common");
  const { isDarkMode } = useTheme();
  const c = ui(isDarkMode);
  const [rows, setRows] = useState<PlatformInvoiceRow[]>([]);
  const [drafts, setDrafts] = useState<Record<string, Draft>>({});
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      const list = await fetchPlatformInvoices({ status: "por_confirmar" });
      setRows(list);
      setDrafts((prev) => Object.fromEntries(list.map((r) => [r.id, prev[r.id] || draftOf(r)])));
    } catch (err) {
      setError(apiErrorMessage(err) || t("expenses.errors.load"));
    }
  }, [t]);

  useEffect(() => {
    if (open) {
      setNotice(null);
      setError(null);
      void load();
    }
  }, [open, load]);

  const onFiles = async (files: File[]) => {
    setBusy(true);
    setError(null);
    try {
      const r = await uploadPlatformInvoices(files);
      const parts = [tp("invoices.uploaded", { count: r.invoices.length })];
      if (r.duplicates.length) parts.push(tp("invoices.duplicates", { count: r.duplicates.length }));
      setNotice(parts.join(" "));
      await load();
      onChanged();
    } catch (err) {
      setError(apiErrorMessage(err) || t("expenses.errors.save"));
    } finally {
      setBusy(false);
    }
  };

  const setDraft = (id: string, k: keyof Draft, v: string) => setDrafts((d) => ({ ...d, [id]: { ...d[id], [k]: v } }));

  const confirm = async (r: PlatformInvoiceRow) => {
    const d = drafts[r.id];
    setError(null);
    try {
      const fromXml = r.data?.readBy === "xml";
      await confirmPlatformInvoice(r.id, {
        platformId: d.platformId,
        expenseId: d.charge && d.charge !== CREATE ? d.charge : null,
        createExpense: d.charge === CREATE,
        data: fromXml
          ? undefined
          : {
              vendor: d.vendor || null,
              invoiceNumber: d.invoiceNumber || null,
              issueDate: d.issueDate || null,
              total: d.total === "" ? null : Number(d.total),
              currency: d.currency || null,
            },
      });
      setNotice(tp("invoices.confirmed"));
      await load();
      onChanged();
    } catch (err) {
      setError(apiErrorMessage(err) || t("expenses.errors.save"));
    }
  };

  const discard = async (r: PlatformInvoiceRow) => {
    if (!window.confirm(tp("invoices.confirmDiscard"))) return;
    try {
      await deletePlatformInvoice(r.id);
      await load();
      onChanged();
    } catch (err) {
      setError(apiErrorMessage(err) || t("expenses.errors.save"));
    }
  };

  const openFile = async (path: string) => {
    try {
      const { url } = await attachmentUrl(path);
      window.open(url, "_blank", "noopener");
    } catch (err) {
      setError(apiErrorMessage(err) || t("expenses.errors.load"));
    }
  };

  const activePlatforms = platforms.filter((p) => p.active !== false);

  return (
    <Dialog open={open} title={tp("invoices.title")} onClose={onClose} wide>
      <div className="space-y-4">
        <ErrorBox message={error} />
        {notice && <Notice tone="ok">{notice}</Notice>}
        <FileDrop
          accept=".pdf,.xml,.png,.jpg,.jpeg,.webp,application/pdf,text/xml,application/xml,image/*"
          multiple
          disabled={busy}
          onFiles={onFiles}
          label={tp("invoices.dropLabel")}
          hint={tp("invoices.dropHint")}
        />
        {busy && <Spinner label={tp("invoices.reading")} />}

        <h3 className={`text-sm font-semibold ${c.text}`}>{tp("invoices.pendingTitle")}</h3>
        {rows.length === 0 && <p className={`text-sm ${c.muted}`}>{tp("invoices.pendingEmpty")}</p>}
        {rows.map((r) => {
          const d = drafts[r.id] || draftOf(r);
          const fromXml = r.data?.readBy === "xml";
          const suggested = r.proposal?.expenseId;
          return (
            <div key={r.id} className={`${c.card} space-y-3`} data-testid={`pending-${r.id}`}>
              <div className="flex flex-wrap items-center gap-2">
                <Chip className={KIND_TONES[r.data?.kind || "factura_extranjera"]}>
                  {tp(`invoices.kind_${r.data?.kind || "factura_extranjera"}`)}
                </Chip>
                <span className={`text-sm font-medium ${c.text}`}>{r.data?.vendor || r.files?.[0]?.name}</span>
                {r.data?.total != null && (
                  <span className={`text-sm tabular-nums ${c.muted}`}>{money(r.data.total, r.data.currency || "MXN", i18n.language)}</span>
                )}
                {r.data?.seats != null && <span className={`text-xs ${c.muted}`}>· {tp("invoices.seats")}: {r.data.seats}</span>}
                {r.data?.usageQuantity != null && (
                  <span className={`text-xs ${c.muted}`}>
                    · {tp("invoices.usage")}: {r.data.usageQuantity} {r.data.usageUnit || ""}
                  </span>
                )}
                {(r.files || []).map((f) => (
                  <button key={f.path} type="button" className="text-xs text-teal-600 hover:underline" onClick={() => openFile(f.path)}>
                    <i className="ri-attachment-2 mr-0.5" />
                    {f.name}
                  </button>
                ))}
              </div>
              {r.warning && <Notice tone="warn">{r.warning}</Notice>}
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-5">
                <Field label={tp("invoices.vendor")} className="sm:col-span-2">
                  <input className={c.input} value={d.vendor} disabled={fromXml} onChange={(e) => setDraft(r.id, "vendor", e.target.value)} />
                </Field>
                <Field label={tp("invoices.number")}>
                  <input className={c.input} value={d.invoiceNumber} disabled={fromXml} onChange={(e) => setDraft(r.id, "invoiceNumber", e.target.value)} />
                </Field>
                <Field label={tp("invoices.issueDate")}>
                  <input type="date" className={c.input} value={d.issueDate} disabled={fromXml} onChange={(e) => setDraft(r.id, "issueDate", e.target.value)} />
                </Field>
                <div className="grid grid-cols-2 gap-2">
                  <Field label={tp("invoices.total")}>
                    <input type="number" min="0" step="0.01" className={c.input} value={d.total} disabled={fromXml} onChange={(e) => setDraft(r.id, "total", e.target.value)} />
                  </Field>
                  <Field label={tp("invoices.currency")}>
                    <select className={c.input} value={d.currency} disabled={fromXml} onChange={(e) => setDraft(r.id, "currency", e.target.value)}>
                      {["USD", "MXN", "EUR"].map((x) => (
                        <option key={x}>{x}</option>
                      ))}
                    </select>
                  </Field>
                </div>
                <Field label={tp("invoices.platform")} className="sm:col-span-2">
                  <select className={c.input} value={d.platformId} onChange={(e) => setDraft(r.id, "platformId", e.target.value)} aria-label={tp("invoices.platform")}>
                    <option value="">{tp("invoices.choosePlatform")}</option>
                    {activePlatforms.map((p) => (
                      <option key={p.id} value={p.id}>
                        {p.name}
                      </option>
                    ))}
                  </select>
                </Field>
                <Field label={tp("invoices.charge")} className="sm:col-span-3">
                  <select className={c.input} value={d.charge} onChange={(e) => setDraft(r.id, "charge", e.target.value)} aria-label={tp("invoices.charge")}>
                    <option value={NO_CHARGE}>{tp("invoices.noCharge")}</option>
                    {(r.proposal?.candidates || []).map((x) => (
                      <option key={x.expenseId} value={x.expenseId}>
                        {`${x.date} · ${x.vendor} · ${money(x.amount, x.currency, i18n.language)}${x.expenseId === suggested ? ` (${tp("invoices.suggested")})` : ""}`}
                      </option>
                    ))}
                    <option value={CREATE}>{tp("invoices.createCharge")}</option>
                  </select>
                </Field>
              </div>
              <div className="flex justify-end gap-2">
                <button type="button" className={c.secondary} onClick={() => discard(r)}>
                  {tp("invoices.discard")}
                </button>
                <button type="button" className={c.primary} disabled={!d.platformId} onClick={() => confirm(r)}>
                  {tp("invoices.confirm")}
                </button>
              </div>
            </div>
          );
        })}
      </div>
    </Dialog>
  );
}
